import React, { Component, Fragment, MouseEvent } from 'react';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';

import { FeedKey, GetGroupFollowersDocument, Group } from 'api';
import { UserGroup } from 'types/app';
import { Alert, FollowToggle } from 'components';
import { isAdminUser } from 'helpers';
import { IWithNotify, IWithUser, withNotify, withUser } from 'providers';
import { generateLink } from 'Links';

import { isGroupOwnerOrModerator } from '../../helpers';

interface IProps {
  selectedGroup: UserGroup;
  enableSort?: boolean;
  sort?: boolean;
  onSort?: () => void;
}

interface IPropsEnhanced extends IProps, IWithNotify, IWithUser {}

interface IState {
  anchorEl: HTMLElement;
  open: boolean;
}

const enhancers = compose<IPropsEnhanced, IProps>(withUser, withNotify);

class FeedMenu extends Component<IPropsEnhanced, IState> {
  static defaultProps: Partial<IProps> = {
    enableSort: false,
  };

  state: IState = {
    anchorEl: null,
    open: false,
  };

  toggleConfirm = () => {
    const { open } = this.state;
    this.setState({ open: !open });
  };

  notify() {
    const { notify, selectedGroup } = this.props;
    const name = selectedGroup.title;

    notify.info({
      i18n: {
        i18nKey: 'feed:menuLeaveGroupNotify',
        values: { name },
      },
    });
  }

  handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null, open: false });
  };

  handleCompleted = () => {
    this.handleClose();
    this.notify();
  };

  handleSort = () => {
    this.handleClose();
    this.props.onSort();
  };

  renderAlert() {
    const { selectedGroup } = this.props;
    const { open } = this.state;

    return (
      <FollowToggle
        source={selectedGroup as Group}
        type={FeedKey.Group}
        refetchQueries={[
          { query: GetGroupFollowersDocument, variables: { id: selectedGroup.id, limit: 10 } },
        ]}>
        {({ onClick }) => (
          <Alert
            open={open}
            onConfirm={() => {
              onClick();
              this.handleCompleted();
            }}
            onCancel={this.toggleConfirm}
            message={<Trans i18nKey="feed:menuLeaveGroupAlert" />}
            confirm={<Trans i18nKey="common:leave" />}
          />
        )}
      </FollowToggle>
    );
  }

  renderGroupLinks() {
    const { selectedGroup, user } = this.props;
    const canEdit = isAdminUser(user) || isGroupOwnerOrModerator(user, selectedGroup.id);
    const canLeave = !isGroupOwnerOrModerator(user, selectedGroup.id);

    return (
      <div>
        {canEdit && (
          <MenuItem component={generateLink(`/group/${selectedGroup.id}/edit`)}>
            <Trans i18nKey="groups:editGroup" />
          </MenuItem>
        )}
        {canLeave && (
          <Fragment>
            {this.renderAlert()}
            <MenuItem onClick={this.toggleConfirm}>
              <Trans i18nKey="feed:menuLeaveGroup" />
            </MenuItem>
          </Fragment>
        )}
      </div>
    );
  }

  renderSort() {
    const { sort } = this.props;

    return (
      <div>
        <MenuItem selected={sort} disabled={sort} onClick={this.handleSort}>
          <Trans i18nKey="feed:menuTrending" />
        </MenuItem>
        <MenuItem selected={!sort} disabled={!sort} onClick={this.handleSort}>
          <Trans i18nKey="feed:menuChronologically" />
        </MenuItem>
      </div>
    );
  }

  render() {
    const { enableSort } = this.props;
    const { anchorEl } = this.state;

    return (
      <Fragment>
        <IconButton onClick={this.handleClick}>
          <MoreHorzIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={this.handleClose}>
          {enableSort && this.renderSort()}
          {this.renderGroupLinks()}
        </Menu>
      </Fragment>
    );
  }
}

export default enhancers(FeedMenu);
